import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Flex, Skeleton } from "antd"
import dayjs from "dayjs"
import { Line } from "react-chartjs-2"
import { useSelector } from "react-redux"

import { tooltipLineWithPoints } from "../../../../DashboardPage/components/chartsPlagins"
import ChartLegend from "../../../components/ChartLegend/ChartLegend"
import VampBlockedBanner from "../VampBlockedBanner/VampBlockedBanner"
import { datasetsOptions, getChartOptions } from "./chartOptions"
import MainChartTooltip from "./MainChartTooltip"

import "./MainChart.less"

const PREV_PREFIX = {
  "previous month": "Prev. month",
  "previous year": "Prev. year",
  "previous week": "Prev. week",
  "previous period": "Prev. period"
}

const getPrevPrefix = (label) => {
  if (!label) return "Prev."
  return PREV_PREFIX[label.trim().toLowerCase()] || "Prev."
}

const MainChart = ({ data, dataType }) => {
  const { theme } = useSelector((state) => state.nav)
  const vampMainChartFetching = useSelector((state) => state.health.vampMainChartFetching)
  const [activeSeries, setActiveSeries] = useState(() => new Set())
  const isSeriesVisible = (id) => activeSeries.size === 0 || activeSeries.has(id)
  const toggleSeries = (id) =>
    setActiveSeries((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const prevPrefix = getPrevPrefix(data?.comparison_period?.label)

  const tooltipRef = useRef()

  const setTooltipImperative = useCallback((p) => {
    tooltipRef.current?.update(p)
  }, [])

  const chartOptions = useMemo(
    () => getChartOptions(theme, setTooltipImperative, dataType),
    [theme, dataType]
  )

  const chartData = useMemo(() => {
    if (!data) return null

    const mainDict = data?.data ?? {}
    const compDict = data?.comparison?.data ?? {}
    const mainLabels = Object.keys(mainDict)
    const compLabels = Object.keys(compDict)

    if (mainLabels.length === 0) return null

    const mainSet = new Set(mainLabels)
    const hasOverlap = compLabels.length > 0 && compLabels.some((l) => mainSet.has(l))

    let labels
    if (hasOverlap) {
      labels = Array.from(new Set([...mainLabels, ...compLabels])).sort()
    } else {
      labels = compLabels.length > mainLabels.length ? compLabels : mainLabels
    }

    const mainStart = mainLabels[0]
    const compStart = compLabels[0]
    const deltaDays = mainStart && compStart ? dayjs(mainStart).diff(dayjs(compStart), "day") : null

    const shadowCompDate = (label) => {
      if (deltaDays == null || !label) return null
      return dayjs(label).subtract(deltaDays, "day").format("YYYY-MM-DD")
    }
    const shadowMainDate = (label) => {
      if (deltaDays == null || !label) return null
      return dayjs(label).add(deltaDays, "day").format("YYYY-MM-DD")
    }

    const compLabelsAligned = hasOverlap
      ? labels.map((l) => (compDict[l] != null ? l : shadowCompDate(l)))
      : labels.map((_, i) => compLabels[i] ?? shadowCompDate(labels[i]))

    const mainLabelsAligned = hasOverlap
      ? labels.map((l) => (mainDict[l] != null ? l : shadowMainDate(l)))
      : labels.map((_, i) => mainLabels[i] ?? shadowMainDate(labels[i]))

    const pickAt = (dict, srcLabels, targetLabels, key) => {
      if (hasOverlap) {
        return targetLabels.map((l) => (dict[l] != null ? dict[l]?.[key] : null))
      }
      return targetLabels.map((_, i) => {
        const l = srcLabels[i]
        return l != null && dict[l] != null ? dict[l]?.[key] : null
      })
    }

    const pointDatasetOptions =
      labels.length === 1
        ? { pointHoverRadius: 6, pointRadius: 4 }
        : { pointRadius: 0, pointHoverRadius: 0 }

    const comparisonOverlayOptions = {
      borderDash: [8, 6],
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 0
    }

    const datasets = []
    const pushSeries = (opts, key) => {
      datasets.push({
        ...opts,
        ...pointDatasetOptions,
        hidden: !isSeriesVisible(opts.datasetType),
        data: pickAt(mainDict, mainLabels, labels, key),
        mainLabels: mainLabelsAligned
      })
      if (compLabels.length) {
        const compType = `${opts.datasetType}_previous`
        datasets.push({
          ...opts,
          ...comparisonOverlayOptions,
          label: `${prevPrefix} ${opts.label}`,
          datasetType: compType,
          hidden: !isSeriesVisible(compType),
          data: pickAt(compDict, compLabels, labels, key),
          comparisonLabels: compLabelsAligned
        })
      }
    }

    const pushThreshold = (opts, value) => {
      datasets.push({
        ...opts,
        ...pointDatasetOptions,
        hidden: !isSeriesVisible(opts.datasetType),
        data: labels.map(() => value)
      })
    }

    if (dataType === "rate") {
      pushSeries(datasetsOptions.vamp_rate, "vamp_rate")
      pushSeries(datasetsOptions.fraud_rate, "fraud_rate")
      pushSeries(datasetsOptions.dispute_rate, "dispute_rate")
      pushThreshold(datasetsOptions.vamp_zone, data?.threshold?.rate)
    } else {
      pushSeries(datasetsOptions.fraud_rate, "fraud_count")
      pushSeries(datasetsOptions.dispute_rate, "dispute_count")
      pushThreshold(datasetsOptions.vamp_zone, data?.threshold?.count)
    }

    return { labels, datasets }
  }, [data, dataType, activeSeries, prevPrefix])

  const legendItems = useMemo(
    () =>
      (chartData?.datasets ?? [])
        .filter((ds) => ds.datasetType)
        .map((ds) => ({
          id: ds.datasetType,
          color: ds.borderColor,
          dashed: Array.isArray(ds.borderDash) && ds.borderDash.length > 0,
          label: ds.label
        })),
    [chartData]
  )

  const legendIdsKey = legendItems.map((i) => i.id).join("|")
  useEffect(() => {
    if (!legendIdsKey) return
    setActiveSeries(new Set(legendIdsKey.split("|")))
  }, [legendIdsKey])

  return (
    <div className="health-monitor-main-chart">
      <MainChartTooltip ref={tooltipRef} dataType={dataType} />

      <div style={{ paddingBottom: 22 }}>
        <Line
          style={{ height: 300 }}
          data={chartData ?? { labels: [], datasets: [] }}
          options={chartOptions}
          plugins={[tooltipLineWithPoints]}
        />
      </div>
      {vampMainChartFetching ? (
        <Flex gap={12} wrap style={{ marginBottom: 12 }}>
          {Array.from({
            length: legendItems.length || (dataType === "rate" ? 4 : 3)
          }).map((_, i) => (
            <Skeleton.Button key={i} active size="small" style={{ width: 110, height: 32 }} />
          ))}
        </Flex>
      ) : (
        legendItems.length > 0 && (
          <ChartLegend
            items={legendItems}
            isActive={(id) => activeSeries.has(id)}
            onToggle={toggleSeries}
            style={{ marginBottom: 12 }}
          />
        )
      )}
      <VampBlockedBanner type="vamp_in_progress" paddingTop={"32px"} />
    </div>
  )
}

export default MainChart
